import { createSelector } from "reselect";

const producersSelect = (state) => state.producers;
const consumersSelect = (state) => state.consumers;
const spotlightsSelector = (state) => state.room.spotlights;
const peersSelector = (state) => state.peers;
const roomSelector = (state) => state.room;
const lobbyPeersSelector = (state) => state.lobbyPeers;
const getPeerConsumers = (state, id) =>
  state.peers[id] ? state.peers[id].consumers : null;
const getAllConsumers = (state) => state.consumers;

const peersKeySelector = createSelector(peersSelector, (peers) =>
  Object.keys(peers)
);

const peersValueSelector = createSelector(peersSelector, (peers) =>
  Object.values(peers)
);

export const peerMessageCountSelector = (id) =>
  createSelector(
    (state) => state,
    (state) => {
      const messages = Object.values(state.chat).filter(
        (c) =>
          c.senderId === id &&
          c.recipient !== "everyone" &&
          c.isUnread &&
          state.me.id === c.recipient
      );
      if (messages) {
        return messages.length;
      } else return 0;
    }
  );

export const peerUnreadPrivateMessageCountSelector = (id) =>
  createSelector(
    (state) => state.chat,
    (chat) => {
      const messages = Object.values(chat).filter(
        (c) => c.recipient === id && c.recipient !== "everyone" && c.isUnread
      );
      if (messages) {
        return messages.length;
      } else return 0;
    }
  );

export const lobbyPeersKeySelector = createSelector(
  lobbyPeersSelector,
  (lobbyPeers) => Object.keys(lobbyPeers)
);

export const micProducersSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).filter((producer) => producer.source === "mic")
);

export const webcamProducersSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).filter((producer) => producer.source === "webcam")
);

export const screenProducersSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).filter((producer) => producer.source === "screen")
);

export const micProducerSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).find((producer) => producer.source === "mic")
);

export const webcamProducerSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).find((producer) => producer.source === "webcam")
);

export const screenProducerSelector = createSelector(
  producersSelect,
  (producers) =>
    Object.values(producers).find((producer) => producer.source === "screen")
);

export const micConsumerSelector = createSelector(
  consumersSelect,
  (consumers) =>
    Object.values(consumers).filter((consumer) => consumer.source === "mic")
);

export const webcamConsumerSelector = createSelector(
  consumersSelect,
  (consumers) =>
    Object.values(consumers).filter((consumer) => consumer.source === "webcam")
);

export const screenConsumerSelector = createSelector(
  consumersSelect,
  (consumers) =>
    Object.values(consumers).filter((consumer) => consumer.source === "screen")
);

export const spotlightsLengthSelector = createSelector(
  spotlightsSelector,
  (spotlights) => spotlights.length
);

export const makeSpotlightPeersSelector = (state) => {
  return createSelector(
    spotlightsSelector,
    peersValueSelector,
    peersKeySelector,
    roomSelector,
    (spotlights, peers, peersKey, room) => {
      const sortedPinnedPeers = peers
        .filter((peer) => peer.visibility)
        .filter((peer) => peer.screenPin)
        .sort((peer1, peer2) => peer1.screenPinIndex - peer2.screenPinIndex)
        .map((peer) => peer.id);
      const unpinnedPeers = peers
        .filter((peer) => peer.visibility)
        .filter((peer) => !peer.screenPin)
        .map((peer) => peer.id);
      if (!room.webinar) {
        return [...sortedPinnedPeers, ...unpinnedPeers].filter((peerId) =>
          spotlights.includes(peerId)
        );
      } else {
        const getPeerConsumers = makePeerConsumerSelector();

        const filteredPeerConsumer = unpinnedPeers.filter((peerId) => {
          let consumers = getPeerConsumers(state, peerId);
          if (consumers === null) return [];
          //let peerObj = state.peersKey.find((p)=> p.id === peerId);

          const remoteMicEnabled =
            Boolean(consumers.micConsumer) &&
            !consumers.micConsumer.remotelyPaused;

          const videoVisible =
            Boolean(consumers.webcamConsumer) &&
            !consumers.webcamConsumer.locallyPaused &&
            !consumers.webcamConsumer.remotelyPaused;

          const screenVisible =
            Boolean(consumers.screenConsumer) &&
            !consumers.screenConsumer.locallyPaused &&
            !consumers.screenConsumer.remotelyPaused;

          return (
            spotlights.includes(peerId) &&
            (videoVisible || remoteMicEnabled || screenVisible)
          );
        });

        return [...sortedPinnedPeers, ...filteredPeerConsumer];
      }
    }
  );
};

export const peersLengthSelector = createSelector(
  peersSelector,
  (peers) => Object.values(peers).length
);

export const raisedHandLengthSelector = createSelector(
  peersSelector,
  (peers) => Object.values(peers).filter((peer) => peer.raiseHandState).length
);

export const makePassivePeersSelector = (state) => {
  return createSelector(
    peersKeySelector,
    makeSpotlightPeersSelector(state),
    (peers, spotlights) =>
      peers.filter((peerId) => !spotlights.includes(peerId))
  );
};

export const videoBoxesSelector = createSelector(
  spotlightsLengthSelector,
  screenProducersSelector,
  screenConsumerSelector,
  (spotlightsLength, screenProducers, screenConsumers) =>
    spotlightsLength + 1 + screenProducers.length + screenConsumers.length
);

export const meProducersSelector = createSelector(
  micProducerSelector,
  webcamProducerSelector,
  screenProducerSelector,
  (micProducer, webcamProducer, screenProducer) => {
    return {
      micProducer,
      webcamProducer,
      screenProducer,
    };
  }
);

export const passiveMicConsumerSelector = createSelector(
  spotlightsSelector,
  consumersSelect,
  (spotlights, consumers) =>
    Object.values(consumers).filter(
      (consumer) =>
        consumer.source === "mic" && !spotlights.includes(consumer.peerId)
    )
);

export const makePeerConsumerSelector = () => {
  return createSelector(
    getPeerConsumers,
    getAllConsumers,
    (consumers, allConsumers) => {
      if (!consumers) return null;

      const consumersArray = consumers.map(
        (consumerId) => allConsumers[consumerId]
      );
      const micConsumer = consumersArray.find(
        (consumer) => consumer.source === "mic"
      );
      const webcamConsumer = consumersArray.find(
        (consumer) => consumer.source === "webcam"
      );
      const screenConsumer = consumersArray.find(
        (consumer) => consumer.source === "screen"
      );

      return { micConsumer, webcamConsumer, screenConsumer };
    }
  );
};
